import { CSSProperties, ReactElement, ReactNode, useRef, useState } from 'react';
import cx from 'clsx';
import {
  autoUpdate,
  FloatingPortal,
  offset,
  safePolygon,
  shift,
  useDismiss,
  useFloating,
  useHover,
  useInteractions,
  useRole,
} from '@floating-ui/react';
import { floatingContainerStyle } from '@ff-it/ui';
import { clampLine } from 'styles/style.css';
import { reveal } from './style.css';

// px — the reveal surface's border width, compensated in the offset so content lines up exactly
const SURFACE_BORDER = 1;
// px — content wider/taller than its box by more than this counts as clipped.
const CLIP_TOLERANCE = 0.005;
// ms — hover dwell before the reveal opens, so a quick sweep across cells doesn't open a trail
const OPEN_DELAY_MS = 80;
// px — keep the reveal at least this far from the viewport edge when it's shifted into view
const VIEWPORT_PADDING = 8;

function isClipped(el: HTMLElement): boolean {
  const box = el.getBoundingClientRect();
  const range = document.createRange();
  range.selectNodeContents(el);
  const content = range.getBoundingClientRect();
  return content.width - box.width > CLIP_TOLERANCE || content.height - box.height > CLIP_TOLERANCE;
}

function snapshotTextStyle(el: HTMLElement): CSSProperties {
  const cs = getComputedStyle(el);
  return {
    fontFamily: cs.fontFamily,
    fontSize: cs.fontSize,
    fontWeight: cs.fontWeight,
    fontStyle: cs.fontStyle,
    lineHeight: cs.lineHeight,
    letterSpacing: cs.letterSpacing,
    color: cs.color,
  };
}

function cellPadding(el: HTMLElement): number {
  const cell = el.closest('td, th');
  if (!cell) {
    return 0;
  }
  return Number.parseFloat(getComputedStyle(cell).paddingTop) || 0;
}

/**
 * Wraps arbitrary content that may be clipped by its column. On hover — but only when the content is actually clipped —
 * it expands in place: an overlay pinned to the same top-left reveals the full content, gaining a background and
 * spilling over neighbouring cells instead of being truncated.
 */
export function OverflowContainer({ children, className }: { children: ReactNode; className?: string }): ReactElement {
  const [open, setOpen] = useState(false);
  const [snapshot, setSnapshot] = useState<{ minWidth: number; text: CSSProperties; pad: number }>({
    minWidth: 0,
    text: {},
    pad: 0,
  });
  const padRef = useRef(0);

  const { refs, floatingStyles, context, isPositioned } = useFloating({
    open,
    onOpenChange: (next) => {
      if (next) {
        const el = refs.reference.current as HTMLElement | null;
        if (!el || !isClipped(el)) {
          return;
        }
        const pad = cellPadding(el);
        padRef.current = pad;
        setSnapshot({ minWidth: el.offsetWidth, text: snapshotTextStyle(el), pad });
      }
      setOpen(next);
    },
    placement: 'bottom-start',
    middleware: [
      offset(({ rects }) => ({
        mainAxis: -rects.reference.height - padRef.current - SURFACE_BORDER,
        crossAxis: -padRef.current - SURFACE_BORDER,
      })),
      shift({ padding: VIEWPORT_PADDING }),
    ],
    whileElementsMounted: autoUpdate,
  });

  const hover = useHover(context, { delay: { open: OPEN_DELAY_MS }, handleClose: safePolygon() });
  const dismiss = useDismiss(context);
  const role = useRole(context, { role: 'tooltip' });
  const { getReferenceProps, getFloatingProps } = useInteractions([hover, dismiss, role]);

  return (
    <>
      <div ref={refs.setReference} className={cx(clampLine, className)} {...getReferenceProps()}>
        {children}
      </div>
      {open && (
        <FloatingPortal>
          <div
            ref={refs.setFloating}
            className={cx(floatingContainerStyle, reveal)}
            style={{
              ...floatingStyles,
              ...snapshot.text,
              minWidth: snapshot.minWidth,
              padding: snapshot.pad,
              visibility: isPositioned ? 'visible' : 'hidden',
            }}
            {...getFloatingProps()}
          >
            {children}
          </div>
        </FloatingPortal>
      )}
    </>
  );
}
